// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0029-principal-scoped-credential-grants.md
import * as React from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { deriveListQuery } from '@/lib/listQuery';
import type {
  CredentialRequiredGroup,
  CredentialRequiredGroupRef,
  GroupListItem,
  IdpGroupRegistration,
} from '@greenlight/shared';

const PAGE_SIZE = 100;

const keyOf = (g: CredentialRequiredGroup): string =>
  g.source === 'greenlight' ? `g:${g.group_id}` : `i:${g.idp_group_registration_id}`;

export function sameRequiredGroups(
  a: readonly CredentialRequiredGroup[],
  b: readonly CredentialRequiredGroup[],
): boolean {
  const keys = new Set(a.map(keyOf));
  return a.length === b.length && b.every((g) => keys.has(keyOf(g)));
}

export function toRequiredGroupRefs(
  groups: readonly CredentialRequiredGroup[],
): CredentialRequiredGroupRef[] {
  return groups.map((g) =>
    g.source === 'greenlight'
      ? { group_id: g.group_id }
      : { idp_group_registration_id: g.idp_group_registration_id },
  );
}

export interface RequiredGroupsPickerProps {
  idPrefix: string;
  value: readonly CredentialRequiredGroup[];
  onChange: (next: CredentialRequiredGroup[]) => void;
  /** Fetch the registry only while the surrounding dialog is open. */
  enabled: boolean;
}

/**
 * Chooses the registered groups a credential requires. Offers Greenlight groups
 * and registered directory groups by name; never a raw directory identifier.
 */
export function RequiredGroupsPicker({
  idPrefix,
  value,
  onChange,
  enabled,
}: RequiredGroupsPickerProps): React.JSX.Element {
  const groupsQuery = api.groups.list.useQuery(
    ['groups'],
    { query: { limit: PAGE_SIZE } },
    { queryKey: ['groups'], enabled },
  );
  const idpQuery = api.groups.listIdpGroups.useQuery(
    ['idp-groups'],
    { query: { limit: PAGE_SIZE } },
    { queryKey: ['idp-groups'], enabled },
  );
  const greenlight = deriveListQuery<GroupListItem>({
    data: groupsQuery.data,
    isError: groupsQuery.isError,
  });
  // An org without a directory-group source answers the registry read with an
  // error; that simply means there are no directory groups to offer.
  const registrations = deriveListQuery<IdpGroupRegistration>({
    data: idpQuery.data,
    isError: idpQuery.isError,
  });

  const options: CredentialRequiredGroup[] = [
    ...greenlight.items.map(
      (g): CredentialRequiredGroup => ({ source: 'greenlight', group_id: g.id, name: g.name }),
    ),
    ...registrations.items.map(
      (r): CredentialRequiredGroup => ({
        source: 'idp',
        idp_group_registration_id: r.id,
        name: r.label,
      }),
    ),
  ].sort((a, b) => a.name.localeCompare(b.name));
  const selected = new Set(value.map(keyOf));

  const toggle = (option: CredentialRequiredGroup, checked: boolean): void => {
    onChange(checked ? [...value, option] : value.filter((g) => keyOf(g) !== keyOf(option)));
  };

  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium">Required groups</legend>
      <p className="text-[11.5px] leading-snug text-muted-foreground">
        Only members may use this credential. An app keeps its grant only while everyone who can
        open it is in one of these groups.
      </p>
      {groupsQuery.isLoading ? (
        <p className="text-[11.5px] text-muted-foreground">Loading groups…</p>
      ) : greenlight.hasError ? (
        <p className="text-[11.5px] text-destructive">Couldn’t load groups.</p>
      ) : options.length === 0 ? (
        <p className="text-[11.5px] text-muted-foreground">
          No groups yet. Create or register one on the Users page.
        </p>
      ) : (
        <ul className="max-h-40 space-y-1 overflow-y-auto" aria-label="Groups">
          {options.map((option) => {
            const id = `${idPrefix}-group-${keyOf(option)}`;
            return (
              <li key={keyOf(option)} className="flex items-center gap-2">
                <Checkbox
                  id={id}
                  checked={selected.has(keyOf(option))}
                  onCheckedChange={(checked) => toggle(option, checked === true)}
                />
                <Label htmlFor={id} className="cursor-pointer font-normal">
                  {option.name}
                  <span className="ml-1.5 text-[11px] text-muted-foreground">
                    {option.source === 'greenlight' ? 'Greenlight group' : 'Directory group'}
                  </span>
                </Label>
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}
